import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Collapse,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
  Inventory as CatalogoIcon,
  Factory as ProduccionIcon,
  Article as ArticulosIcon,
  Business as ProveedoresIcon,
  Group as AsociacionIcon,
  SetMeal as EspeciesIcon,
  Label as MarcasIcon,
  Description as DERCIcon,
  LocalShipping as GuiasIcon,
  Assignment as PartesIcon,
  Person as DuenioIcon,
  SupervisedUserCircle as EncargadoIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  NotificationsOutlined,
  LightModeOutlined,
  DarkModeOutlined,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  Place as ProcedenciaIcon,
  SwapHoriz as DevolucionIcon,
  Inventory2 as ProductoBIcon,
  ExitToApp as SalidaIcon,
  Receipt as LiquidacionIcon,
  PriceChange as PreciosIcon,
  AdminPanelSettings as AdminIcon,
  ReceiptLong as FacturacionIcon,
  PriceCheck as PreciosClienteIcon,
} from '@mui/icons-material';
import { useAuth } from '../../auth/hooks/useAuth';
import { usePermissions } from '../../auth/hooks/usePermissions';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

interface SidebarProps {
  mobileOpen: boolean;
  onMobileToggle: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  darkMode: boolean;
  onThemeToggle: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  module?: string; // Modulo requerido: CATALOGO, PRODUCCION, SEGURIDAD, REPORTES, SISTEMA
}

const menuItems: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  {
    id: 'catalogo',
    label: 'Catalogo',
    icon: <CatalogoIcon />,
    module: 'CATALOGO',
    children: [
      { id: 'articulos', label: 'Articulos', icon: <ArticulosIcon />, path: '/catalogo/articulos' },
      { id: 'marcas', label: 'Marcas', icon: <MarcasIcon />, path: '/catalogo/marcas' },
      { id: 'proveedores', label: 'Proveedores', icon: <ProveedoresIcon />, path: '/catalogo/proveedores' },
      { id: 'asociacion', label: 'Asociaciones', icon: <AsociacionIcon />, path: '/catalogo/asociacion' },
      { id: 'especies', label: 'Especies', icon: <EspeciesIcon />, path: '/catalogo/especies' },
      { id: 'duenios', label: 'Duenos', icon: <DuenioIcon />, path: '/catalogo/duenios' },
      { id: 'encargados', label: 'Encargados', icon: <EncargadoIcon />, path: '/catalogo/encargados' },
      { id: 'procedencias', label: 'Procedencias', icon: <ProcedenciaIcon />, path: '/catalogo/procedencias' },
    ],
  },
  {
    id: 'produccion',
    label: 'Produccion',
    icon: <ProduccionIcon />,
    module: 'PRODUCCION',
    children: [
      { id: 'derc', label: 'DERC', icon: <DERCIcon />, path: '/produccion/derc' },
      { id: 'guias-materia-prima', label: 'Guias Materia Prima', icon: <GuiasIcon />, path: '/produccion/guias-materia-prima' },
      { id: 'guias-produccion', label: 'Guias de Produccion', icon: <GuiasIcon />, path: '/produccion/guias-produccion' },
      { id: 'partes-produccion', label: 'Partes de Produccion', icon: <PartesIcon />, path: '/produccion/partes-produccion' },
      { id: 'guias-salida', label: 'Guias de Salida', icon: <SalidaIcon />, path: '/produccion/guias-salida' },
      { id: 'devoluciones-internas', label: 'Dev. Internas', icon: <DevolucionIcon />, path: '/produccion/devoluciones-internas' },
      { id: 'productos-tipo-b', label: 'Producto B', icon: <ProductoBIcon />, path: '/produccion/productos-tipo-b' },
    ],
  },
  {
    id: 'administracion',
    label: 'Administracion',
    icon: <AdminIcon />,
    module: 'ADMINISTRACION',
    children: [
      { id: 'orden-liquidacion', label: 'Orden Liquidación', icon: <LiquidacionIcon />, path: '/administracion/orden-liquidacion' },
      { id: 'orden-facturacion', label: 'Orden Facturación', icon: <FacturacionIcon />, path: '/administracion/orden-facturacion' },
      { id: 'precios-proveedor', label: 'Precios Proveedor', icon: <PreciosIcon />, path: '/administracion/precios-proveedor' },
      { id: 'precios-cliente', label: 'Precios Cliente', icon: <PreciosClienteIcon />, path: '/administracion/precios-cliente' },
    ],
  },
  {
    id: 'seguridad',
    label: 'Seguridad',
    icon: <SecurityIcon />,
    module: 'SEGURIDAD',
    children: [
      { id: 'usuarios', label: 'Usuarios', icon: <PeopleIcon />, path: '/seguridad/usuarios' },
      { id: 'roles', label: 'Roles', icon: <ShieldIcon />, path: '/seguridad/roles' },
      { id: 'permisos', label: 'Permisos', icon: <LockIcon />, path: '/seguridad/permisos' },
    ],
  },
  { id: 'reports', label: 'Reports', icon: <ReportsIcon />, path: '/reports', module: 'REPORTES' },
  { id: 'settings', label: 'Settings', icon: <SettingsIcon />, path: '/settings', module: 'SISTEMA' },
];

const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen,
  onMobileToggle,
  collapsed = false,
  onToggleCollapse,
  darkMode,
  onThemeToggle
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = React.useState<Record<string, boolean>>({});
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const { permissions, isSuperAdmin } = usePermissions();

  // Debug: mostrar permisos en consola
  React.useEffect(() => {
    console.log('=== DEBUG PERMISOS ===');
    console.log('Usuario:', user?.name, '- Rol:', user?.role);
    console.log('Permisos del token:', permissions);
    console.log('Es SuperAdmin:', isSuperAdmin());
  }, [permissions, user, isSuperAdmin]);

  // Filtrar menu items basado en permisos
  const hasModuleAccess = (module?: string): boolean => {
    // Sin modulo definido = acceso libre (dashboard)
    if (!module) return true;
    // SuperAdmin tiene acceso a todo
    if (isSuperAdmin()) return true;
    // Verificar si tiene algun permiso del modulo
    // Para ADMINISTRACION, también verificar permisos de LIQUIDACION y FACTURACION
    const modulePermissionMap: Record<string, string[]> = {
      'ADMINISTRACION': ['LIQUIDACION', 'FACTURACION', 'ADMINISTRACION'],
    };
    const permissionPrefixes = modulePermissionMap[module] || [module];
    const hasAccess = permissions.some((p: string) =>
      permissionPrefixes.some(prefix => p.startsWith(prefix) || p.includes(prefix))
    );
    return hasAccess;
  };

  const filteredMenuItems = menuItems.filter(item => hasModuleAccess(item.module));

  const handleItemClick = (item: MenuItem) => {
    if (item.children) {
      setOpenMenus(prev => ({
        ...prev,
        [item.id]: !prev[item.id]
      }));
    } else if (item.path) {
      navigate(item.path);
      if (isMobile) {
        onMobileToggle();
      }
    }
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  const isItemSelected = (item: MenuItem) => {
    if (item.path && location.pathname === item.path) return true;
    if (item.children) {
      return item.children.some(child => child.path && location.pathname === child.path);
    }
    return false;
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => (
    <React.Fragment key={item.id}>
      <ListItem disablePadding sx={{ px: collapsed ? 0.5 : 1 }}>
        <ListItemButton
          selected={isItemSelected(item)}
          onClick={() => handleItemClick(item)}
          sx={{
            borderRadius: 2,
            mx: collapsed ? 0.5 : 1,
            mb: 0.5,
            pl: level === 0 ? (collapsed ? 1 : 1) : 3,
            justifyContent: collapsed ? 'center' : 'flex-start',
            '&.Mui-selected': {
              backgroundColor: '#3b82f6',
              '&:hover': {
                backgroundColor: '#2563eb',
              },
            },
            '&:hover': {
              backgroundColor: '#334155',
            },
          }}
        >
          <ListItemIcon sx={{ 
            color: 'inherit', 
            minWidth: collapsed ? 'unset' : 40,
            mr: collapsed ? 0 : undefined,
            justifyContent: 'center'
          }}>
            {item.icon}
          </ListItemIcon>
          {!collapsed && (
            <>
              <ListItemText 
                primary={item.label}
                slotProps={{
                  primary: {
                    fontSize: level === 0 ? '0.875rem' : '0.8rem',
                    fontWeight: isItemSelected(item) ? 600 : 400,
                  }
                }}
              />
              {item.children && (
                openMenus[item.id] ? <ExpandLess /> : <ExpandMore />
              )}
            </>
          )}
        </ListItemButton>
      </ListItem>
      
      {item.children && !collapsed && (
        <Collapse in={openMenus[item.id]} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map((child) => renderMenuItem(child, 1))}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        p: collapsed ? 1 : 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between'
      }}>
        {!collapsed && (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#3b82f6' }}>
              Mantis
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Admin Dashboard
            </Typography>
          </Box>
        )}
        {!isMobile && onToggleCollapse && (
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            sx={{
              color: '#94a3b8',
              '&:hover': {
                backgroundColor: '#334155',
                color: 'white',
              },
            }}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}
      </Box>
      
      <Divider sx={{ borderColor: '#334155' }} />
      
      {/* Menu items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ pt: 2 }}>
          {filteredMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      {/* User section */}
      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ borderColor: '#334155' }} />

        {/* Theme and notifications */}
        <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'space-around', p: collapsed ? 1 : 2 }}>
          <IconButton
            size="small"
            sx={{ color: '#94a3b8' }}
          >
            <NotificationsOutlined />
          </IconButton>

          <IconButton
            size="small"
            onClick={onThemeToggle}
            sx={{
              color: '#94a3b8',
              '&:hover': {
                color: 'white',
              },
            }}
            aria-label="toggle theme"
          >
            {darkMode ? <LightModeOutlined /> : <DarkModeOutlined />}
          </IconButton>
        </Box>

        {/* User info and menu */}
        <Box sx={{ p: collapsed ? 1 : 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: collapsed ? 0 : 1.5,
              justifyContent: collapsed ? 'center' : 'flex-start',
              cursor: 'pointer',
              borderRadius: 2,
              p: 1,
              '&:hover': {
                backgroundColor: '#334155',
              },
            }}
            onClick={handleMenu}
          >
            <Avatar
              sx={{
                width: collapsed ? 36 : 40,
                height: collapsed ? 36 : 40,
                backgroundColor: '#3b82f6',
                fontSize: '1rem',
              }}
              src={user?.avatar}
            >
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>

            {!collapsed && (
              <Box sx={{ textAlign: 'left', flexGrow: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'white' }}>
                  {user?.name || 'Usuario'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  {user?.role || 'Sin rol'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                mb: 1,
                minWidth: 200,
              },
            },
          }}
        >
          <MenuItem onClick={handleClose}>
            <ListItemIcon>
              <AccountIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Perfil</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleClose}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Configuración</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cerrar Sesión</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: collapsed ? collapsedDrawerWidth : drawerWidth }, flexShrink: { md: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            backgroundColor: '#1e293b',
            color: 'white',
            borderRight: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: collapsed ? collapsedDrawerWidth : drawerWidth,
            backgroundColor: '#1e293b',
            color: 'white',
            borderRight: 'none',
            transition: 'width 0.2s ease-in-out',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export { drawerWidth, collapsedDrawerWidth };
export default Sidebar;